import { IDealState } from '../reducers/currentDealReducer';
import { current, PayloadAction } from '@reduxjs/toolkit';
import ICarConfigOption from '../../../../../common/model/ICarConfigOption';
import { OptionType } from '../../../common/enum/OptionType';
import { IPrice, IPrices } from '../../../../../common/model/IPrice';
import { DealPriceType } from '../../../../../common/enum/DealPriceType';
import financeQuoteTransformer from '../../../common/service/financeQuoteTransformer';
import dealPriceAccessor from '../../../common/service/dealPriceAccessor';
import carPriceCalculator from '../../../common/service/carPriceCalculator';
import { IDealThunk } from '../../model/IThunkData';

const currentDealServices = {
  setServicePlan: (state: IDealState, action: PayloadAction<IDealThunk>) => {
    // Set service plan
    const servicePlan: ICarConfigOption | null = action.payload.deal.fullProductConfiguration.options.find(
      (option: ICarConfigOption) =>
        option.category.category && option.category.category.toLowerCase() === OptionType.SERVICE_PLAN.toLowerCase(),
    );
    if (!servicePlan) {
      state.servicePlan = null;
    }
    const getFittedPrice = (prices: IPrice[]): IPrice => {
      return prices.find((price: IPrice) => price.type === DealPriceType.FITTED);
    };
    if (servicePlan?.prices) {
      const { basePrice: servicePlanBasePrice } = getFittedPrice(servicePlan.prices);
      state.prices.servicePlanPrice = servicePlanBasePrice || 0;
      state.servicePlan = servicePlan;

      return;
    }

    // Reset values if no service plan is found on deal
    state.prices.servicePlanPrice = 0;
    state.servicePlan = null;
  },

  setFinanceQuotes: (state: IDealState, action: PayloadAction<IDealThunk>) => {
    state.financeQuote = financeQuoteTransformer.from(action.payload.deal.financeQuote);
    state.secondaryFinanceQuote = financeQuoteTransformer.from(action.payload.deal.financeQuote);
  },

  setOptions: (state: IDealState, action: PayloadAction<IDealThunk>) => {
    state.accessories = action.payload.deal.fullProductConfiguration.options.filter(
      (option: ICarConfigOption) => option.category.category.toLowerCase() === OptionType.ACCESSORY.toLowerCase(),
    );
    state.accessoryPacks = action.payload.deal.fullProductConfiguration.options.filter(
      (option: ICarConfigOption) => option.category.category.toLowerCase() === OptionType.PACK.toLowerCase(),
    );
  },

  getTotalPrice: (state: IDealState, action: PayloadAction<IDealThunk>, carPrices: IPrices) => {
    return carPrices.find(price => {
      if (current(state).prices.isMultiCurrency) {
        return (
          price.currency === action.payload.priceDisplay.primary.currency && price.type === DealPriceType.VEHICLE_TOTAL
        );
      }
      return price.type === DealPriceType.VEHICLE_TOTAL;
    }) as IPrice;
  },

  getTotalAmountPayable: (state: IDealState, action: PayloadAction<IDealThunk>, carPrices: IPrices) => {
    const primaryTotalPrice = dealPriceAccessor.getTotalPrice(carPrices, action.payload.priceDisplay);
    const getServicePlan: () => ICarConfigOption | null = () =>
      action.payload.deal.fullProductConfiguration.options.find(
        (option: ICarConfigOption) =>
          option.category.category && option.category.category.toLowerCase() === OptionType.SERVICE_PLAN.toLowerCase(),
      );
    const hasServicePlan = Boolean(getServicePlan());
    const { basePrice: servicePlanBasePrice } = hasServicePlan
      ? dealPriceAccessor.getFittedPrice(getServicePlan().prices)
      : { basePrice: 0 };

    return carPriceCalculator.getTotalPayable(
      primaryTotalPrice,
      servicePlanBasePrice,
      action.payload.deal.financeQuote.deposit,
    );
  },

  setCarPrices: (state: IDealState, action: PayloadAction<IDealThunk>) => {
    const carPrices: IPrices = action.payload.deal.fullProductConfiguration?.prices ?? [];

    if (carPrices) {
      state.prices.carPrices = carPrices;
      state.prices.primaryPrice = dealPriceAccessor.getPrimaryPrice(carPrices, action.payload.priceDisplay);
      state.prices.secondaryPrice = dealPriceAccessor.getSecondaryPrice(carPrices, action.payload.priceDisplay);
      state.prices.primaryTotalPrice = dealPriceAccessor.getTotalPrice(carPrices, action.payload.priceDisplay);
      state.prices.secondaryTotalPrice = dealPriceAccessor.getSecondaryTotalPrice(
        carPrices,
        action.payload.priceDisplay,
      );
      state.prices.secondaryTotalPrice = dealPriceAccessor.getSecondaryTotalPrice(
        carPrices,
        action.payload.priceDisplay,
      );
      state.prices.totalPrice = currentDealServices.getTotalPrice(state, action, carPrices);
      state.prices.totalAmountPayable = currentDealServices.getTotalAmountPayable(state, action, carPrices);
    }
  },
};

export default currentDealServices;
